import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fabric } from 'fabric';
import jsPDF from 'jspdf';
import api, { unwrap } from '../utils/api';
import { loadFontsForFields } from '../utils/fontLoader';
import { isCustomId, customFieldPayload, customFieldsFromValues } from '../utils/customFields';
import { addBrandingLogoToCanvas, addOverlayToCanvas } from '../utils/templateRender';
import { COLOR_THEMES, hexToHue, filterFor, swatchColorFor, themesEnabledFor } from '../utils/colorThemes';
import { cleanScale, cleanOffset, cleanColor, fillUnderFilter, textForRender, fieldBox, fitSingleLineSize } from '../utils/fieldSizing';

// Admin order-template editor. Reopens the exact invitation a customer
// bought — rendered from the order's frozen templateSnapshot — so the admin
// can fix a typo in their text and re-download. Saving writes the correction
// back to the order, so the customer's own download link reflects the fix.

const SENTINEL = '__SIMCHA_CLEARED__';
const cleanValue = (v) => (!v || v === SENTINEL ? '' : v);

// Box + font size for a field at the customer's "Text size" multiplier and
// "Change position" offset. Single-line fields shrink to fit their (possibly
// widened) box; multi-line keep size and wrap. Same rules as the customer
// editor and download page.
function layoutField(field, text, scale, displayScale, canvasW, canvasH, offset) {
  const box = fieldBox(field, scale, displayScale, canvasW, canvasH, offset);
  const baseSize = (field.fontSize || 24) * displayScale * scale;
  const fontSize = field.multiLine
    ? baseSize
    : fitSingleLineSize(text, baseSize, box.width, field.fontFamily, field.charSpacing || 0);
  return { ...box, fontSize };
}

// The customer's per-field sizes and positions ride along unchanged so a
// correction never resets them.
const toFieldValues = (vals, scales, offsets, fills, customDefs = {}) => Object.entries(vals).map(([fieldId, value]) => {
  const fv = { fieldId, value };
  if (customDefs[fieldId]) fv.customField = customFieldPayload(customDefs[fieldId]);
  if (scales[fieldId] && scales[fieldId] !== 1) fv.fontScale = scales[fieldId];
  const o = offsets[fieldId];
  if (o && o.x) fv.offsetX = o.x;
  if (o && o.y) fv.offsetY = o.y;
  if (fills[fieldId]) fv.fill = fills[fieldId];
  return fv;
});
const offsetOf = (fv) => ({ x: cleanOffset(fv.offsetX), y: cleanOffset(fv.offsetY) });

export default function OrderItemEditor() {
  const { orderId, itemId } = useParams();
  const canvasElRef = useRef(null);
  const canvasRef = useRef(null);
  const tbRefs = useRef({});
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [scales, setScales] = useState({});
  const [offsets, setOffsets] = useState({});
  const [fills, setFills] = useState({});
  const [themeId, setThemeId] = useState('orig');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- load the order item (snapshot preferred, live fetch as fallback) ----
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/orders/GetOrderItem/${orderId}/${itemId}`);
        const payload = unwrap(r);
        const item = payload.item;

        let product, fields, branding, fontCatalog;
        const snap = item.templateSnapshot;
        if (snap && Array.isArray(snap.fields) && snap.fields.length) {
          product = {
            name: snap.productName,
            imagePath: snap.imagePath,
            brandingColor: snap.brandingColor,
            brandingPosition: snap.brandingPosition || {},
            overlayImages: snap.overlayImages || [],
            overlayImage: snap.overlayImage || null,
            themeBaseColor: snap.themeBaseColor || null,
          };
          fields = snap.fields;
          branding = snap.branding || null;
          fontCatalog = snap.fonts || [];
        } else {
          // Legacy order with no snapshot — best-effort live fetch.
          const pid = item.productId?._id || item.productId;
          const [pr, fr, fonts, brand] = await Promise.all([
            api.get(`/products/GetProduct/${pid}`),
            api.get(`/fields/GetProductFields/${pid}`),
            api.get('/files/GetAllFonts'),
            api.get('/miscellaneous/branding').catch(() => ({ data: { body: null } })),
          ]);
          const p = unwrap(pr) || {};
          product = {
            name: p.name,
            imagePath: p.filesId?.path || p.filesId?.pathWithWatermark || '',
            brandingColor: p.brandingColor,
            brandingPosition: p.brandingPosition || {},
            overlayImages: p.overlayImages || [],
            overlayImage: p.overlayImage || null,
            themeBaseColor: p.themeBaseColor || null,
          };
          fields = unwrap(fr) || [];
          fontCatalog = unwrap(fonts) || [];
          branding = unwrap(brand);
        }
        // Text boxes the customer added in the editor ("+"), edited like fields.
        fields = [...(fields || []), ...customFieldsFromValues(item.fieldValues)];
        await loadFontsForFields(fields, fontCatalog);

        const fvMap = {};
        const scaleMap = {};
        const offsetMap = {};
        const fillMap = {};
        for (const fv of (item.fieldValues || [])) {
          fvMap[fv.fieldId] = cleanValue(fv.value);
          if (Number(fv.fontScale) > 0) scaleMap[fv.fieldId] = cleanScale(fv.fontScale);
          if (fv.offsetX || fv.offsetY) offsetMap[fv.fieldId] = offsetOf(fv);
          if (cleanColor(fv.fill)) fillMap[fv.fieldId] = cleanColor(fv.fill);
        }
        const initial = {};
        for (const f of fields) initial[f._id] = fvMap[f._id] != null ? fvMap[f._id] : '';
        setValues(initial);
        setScales(scaleMap);
        setOffsets(offsetMap);
        setFills(fillMap);
        setThemeId(item.colorTheme || 'orig');
        setData({ ...payload, product, fields, branding });
      } catch (e) {
        const m = e?.response?.data?.body;
        setErr(typeof m === 'string' ? m : 'Could not load this order item.');
      }
    })();
  }, [orderId, itemId]);

  // ---- build the Fabric canvas once the data is loaded ----
  useEffect(() => {
    if (!data) return;
    const { product, fields, branding, item } = data;
    const w = (fields.find(f => f.canvasWidth)?.canvasWidth) || 800;
    const h = (fields.find(f => f.canvasHeight)?.canvasHeight) || 1035;
    const maxW = Math.min(w, Math.max(320, (window.innerWidth || 1200) - 460));
    const displayScale = maxW / w;
    const displayW = w * displayScale;
    const displayH = h * displayScale;

    const canvas = new fabric.Canvas(canvasElRef.current, { width: displayW, height: displayH, backgroundColor: '#fff', selection: false });
    canvasRef.current = canvas;
    canvas._originalW = w;
    canvas._originalH = h;
    canvas._displayScale = displayScale;

    if (product.imagePath) {
      fabric.Image.fromURL(product.imagePath, (img) => {
        img.scaleToWidth(displayW);
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
      }, { crossOrigin: 'anonymous' });
    }
    if (branding?.imageUrl) {
      addBrandingLogoToCanvas(canvas, branding.imageUrl, product.brandingColor || '#FFFFFF', displayW, displayH, product.brandingPosition || {});
    }
    const overlays = (product.overlayImages || []).filter(o => o && o.url);
    if (overlays.length) overlays.forEach((o, i) => addOverlayToCanvas(canvas, o, displayW, displayH, `o-${i}`));
    else if (product.overlayImage?.url) addOverlayToCanvas(canvas, product.overlayImage, displayW, displayH);

    const fvMap = {};
    const scaleMap = {};
    const offsetMap = {};
    for (const fv of (item.fieldValues || [])) {
      fvMap[fv.fieldId] = cleanValue(fv.value);
      scaleMap[fv.fieldId] = cleanScale(fv.fontScale);
      offsetMap[fv.fieldId] = offsetOf(fv);
    }

    tbRefs.current = {};
    for (const f of fields) {
      // Non-editable fields fall back to their originalText when an older
      // order has no captured value for them, so the design isn't blank.
      let text = fvMap[f._id]
        || (f.editable === false ? (f.originalText || f.text || '') : '');
      text = textForRender(text);
      if (f.isUpperCase) text = text.toUpperCase();
      const custom = f.editable !== false;
      const lay = layoutField(f, text, custom ? (scaleMap[f._id] || 1) : 1, displayScale, displayW, displayH, custom ? offsetMap[f._id] : null);
      const tb = new fabric.Textbox(text, {
        objectCaching: false,
        left: lay.left, top: lay.top,
        width: lay.width,
        fontSize: lay.fontSize, fontFamily: f.fontFamily || 'Nexa',
        fontWeight: f.fontWeight || 'normal', fill: f.fill || '#000',
        textAlign: f.textAlign || 'right', angle: f.angle || 0,
        scaleX: f.scaleX || 1, scaleY: f.scaleY || 1,
        charSpacing: f.charSpacing || 0, lineHeight: f.lineHeight || 1,
        originX: f.originX || 'left', originY: f.originY || 'top',
        direction: f.direction || 'rtl',
        splitByGrapheme: !!f.splitByGrapheme,
        underline: !!f.underline, overline: !!f.overline, linethrough: !!f.linethrough,
        backgroundColor: f.backgroundColor || '',
        opacity: f.opacity ?? 1,
        selectable: false, evented: false,
      });
      if (typeof tb.initDimensions === 'function') tb.initDimensions();
      tb.setCoords();
      canvas.add(tb);
      tbRefs.current[f._id] = tb;
    }
    canvas.requestRenderAll();

    // Re-shape every textbox once fonts are confirmed ready.
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!canvasRef.current) return;
        canvasRef.current.getObjects().forEach(obj => {
          if (obj.type === 'textbox') {
            obj._textLines = [];
            obj._unwrappedTextLines = null;
            if (typeof obj.initDimensions === 'function') obj.initDimensions();
            obj.setCoords();
          }
        });
        canvasRef.current.requestRenderAll();
      }).catch(() => {});
    }

    return () => { canvas.dispose(); canvasRef.current = null; };
    // eslint-disable-next-line
  }, [data]);

  const themeFilterNow = filterFor(
    hexToHue(data?.product?.themeBaseColor),
    (COLOR_THEMES.find(t => t.id === themeId) || COLOR_THEMES[0]).targetHue,
  );
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    for (const f of data.fields) {
      const tb = tbRefs.current[f._id];
      if (!tb) continue;
      const custom = f.editable !== false && fills[f._id];
      tb.set({ fill: custom ? fillUnderFilter(fills[f._id], themeFilterNow) : (f.fill || '#000') });
    }
    canvas.requestRenderAll();
  }, [data, fills, themeFilterNow]);

  function updateField(fieldId, raw) {
    const f = data?.fields.find(x => String(x._id) === String(fieldId));
    const tb = tbRefs.current[fieldId];
    const canvas = canvasRef.current;
    if (!f || !tb || !canvas) return;
    let text = textForRender(cleanValue(raw));
    if (f.isUpperCase) text = text.toUpperCase();
    const ds = canvas._displayScale || 1;
    const custom = f.editable !== false;
    const lay = layoutField(f, text, custom ? (scales[fieldId] || 1) : 1, ds, canvas.getWidth(), canvas.getHeight(), custom ? offsets[fieldId] : null);
    tb.set({ text, fontSize: lay.fontSize, left: lay.left, top: lay.top, width: lay.width });
    if (typeof tb.initDimensions === 'function') tb.initDimensions();
    tb.setCoords();
    canvas.requestRenderAll();
  }

  function onChange(fieldId, v) {
    setValues(prev => ({ ...prev, [fieldId]: v }));
    updateField(fieldId, v);
  }

  const product = data?.product;
  const theme = COLOR_THEMES.find(t => t.id === themeId) || COLOR_THEMES[0];
  const sourceHue = hexToHue(product?.themeBaseColor);
  const activeFilter = filterFor(sourceHue, theme.targetHue);
  const themesEnabled = themesEnabledFor(product?.themeBaseColor);

  // Render the Fabric composite, baking in the theme filter via a temp canvas.
  function renderToDataURL(multiplier) {
    const canvas = canvasRef.current;
    const fabricEl = canvas.toCanvasElement(multiplier);
    if (!activeFilter || activeFilter === 'none') return fabricEl.toDataURL('image/png');
    const out = document.createElement('canvas');
    out.width = fabricEl.width;
    out.height = fabricEl.height;
    const ctx = out.getContext('2d');
    ctx.filter = activeFilter;
    ctx.drawImage(fabricEl, 0, 0);
    ctx.filter = 'none';
    return out.toDataURL('image/png');
  }

  function downloadPNG() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scale = (canvas._originalW || canvas.getWidth()) / canvas.getWidth();
    const a = document.createElement('a');
    a.href = renderToDataURL(scale * 2);
    a.download = `${product?.name || 'invitation'}.png`;
    a.click();
  }

  function downloadPDF() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const origW = canvas._originalW || canvas.getWidth();
    const origH = canvas._originalH || canvas.getHeight();
    const scale = origW / canvas.getWidth();
    const dataUrl = renderToDataURL(scale * 2);
    const pdf = new jsPDF({ orientation: origH > origW ? 'portrait' : 'landscape', unit: 'px', format: [origW, origH] });
    pdf.addImage(dataUrl, 'PNG', 0, 0, origW, origH);
    pdf.save(`${product?.name || 'invitation'}.pdf`);
  }

  async function save() {
    setSaving(true);
    setMsg('');
    try {
      await api.post('/orders/UpdateOrderItemValues', { orderId, itemId, fieldValues: toFieldValues(values, scales, offsets, fills, Object.fromEntries(data.fields.filter(f => isCustomId(f._id)).map(f => [f._id, f]))), colorTheme: themeId });
      setMsg('Saved — the customer’s download link now shows the corrected version.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setMsg('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (err) return (
    <>
      <h1>Order template</h1>
      <p className="error-text" style={{ color: '#b42318' }}>{err}</p>
      <Link className="btn secondary" to="/pages/OrderList">Back to orders</Link>
    </>
  );
  if (!data) return <p>Loading order template…</p>;

  const editableFields = (data.fields || [])
    .slice()
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

  return (
    <>
      <h1>Fix order template · {product?.name}</h1>
      <p style={{ color: '#666', marginTop: -8 }}>
        Order <code>{String(orderId).slice(-8)}</code>
        {data.customerEmail ? <> · {data.customerEmail}</> : null}
        {' '}· Editing here updates the customer’s saved order and their download link.
      </p>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Field inputs */}
        <div className="card" style={{ flex: '1 1 360px', minWidth: 320, maxWidth: 460 }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <button className="btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save corrections'}</button>
            <button className="btn secondary" onClick={downloadPNG}>Download PNG</button>
            <button className="btn secondary" onClick={downloadPDF}>Download PDF</button>
          </div>
          {msg && <p style={{ color: msg.startsWith('Saved') ? '#198754' : '#b42318', fontSize: 13 }}>{msg}</p>}

          {themesEnabled && (
            <div className="prop">
              <label>Color theme</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {COLOR_THEMES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setThemeId(t.id)}
                    title={t.name}
                    style={{
                      width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                      background: swatchColorFor(t, product?.themeBaseColor),
                      border: t.id === themeId ? '3px solid #1990C7' : '2px solid #ddd',
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {editableFields.map((f, i) => (
            <div className="prop" key={f._id}>
              <label style={{ fontSize: 12, color: '#888' }}>
                {isCustomId(f._id) ? 'Text box added by the customer' : <>Field {i + 1}{f.originalText ? ` — “${String(f.originalText).slice(0, 40)}”` : ''}</>}
              </label>
              {f.multiLine || String(values[f._id] || '').includes('\n') ? (
                <textarea
                  rows={Math.max(2, String(values[f._id] || '').split('\n').length)}
                  value={values[f._id] || ''}
                  dir={f.direction || 'rtl'}
                  onChange={e => onChange(f._id, e.target.value)}
                />
              ) : (
                <input
                  type="text"
                  value={values[f._id] || ''}
                  dir={f.direction || 'rtl'}
                  onChange={e => onChange(f._id, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Canvas preview */}
        <div className="card" style={{ flex: '2 1 480px', background: '#f5f5f5', display: 'flex', justifyContent: 'center', overflow: 'auto', maxHeight: '82vh' }}>
          <div style={{ filter: activeFilter === 'none' ? undefined : activeFilter, transition: 'filter .25s ease' }}>
            <canvas ref={canvasElRef} style={{ display: 'block' }} />
          </div>
        </div>
      </div>
    </>
  );
}
